/**
 * View Transcriptions Page
 * Browse and view completed transcription results
 * S3-driven navigation with backend access control for matter filtering
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { HierarchicalBrowser, BrowserSelection } from '../components/echo/HierarchicalBrowser';
import { TranscriptionViewer } from '../components/echo/TranscriptionViewer';
import { useS3Content } from '../hooks/useS3Content';
import { useAccessToken } from '../auth/hooks';

/**
 * Page for browsing and viewing transcription results
 */
export function ViewTranscriptionsPage() {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const s3Content = useS3Content();
  const token = useAccessToken();
  const locationHandled = useRef(false);
  const isDirectNav = useRef(!!searchParams.get('location'));

  const [selection, setSelection] = React.useState<BrowserSelection>({
    source: null,
    session: null,
    title: null,
  });

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  // Handle ?location= query param for direct navigation from history page
  // Wait for token to be available before making API calls
  useEffect(() => {
    if (!token) return;
    const location = searchParams.get('location');
    if (location && !locationHandled.current) {
      locationHandled.current = true;

      // Parse the location to set selection state for the dropdowns
      const relativePath = location.replace(/^s3:\/\/[^/]+\//, '');
      const parts = relativePath.split('/').filter(Boolean);

      const source = parts[0] || null;
      const session = parts[1] || null;

      setSelection({ source, session, title: null });

      // Load content, then populate dropdowns
      s3Content.loadContentFromPath(location).then(() => {
        s3Content.loadSources();
        if (source) s3Content.loadSessions(source);
        if (source && session) s3Content.loadTitles(source, session);
      });
    }
  }, [searchParams, token]);

  // Load sources on mount (only if not handling a direct location)
  useEffect(() => {
    if (!token) return;
    if (!isDirectNav.current) {
      s3Content.loadSources();
    }
  }, [token]);

  // Load sessions when source changes (skip on initial direct nav)
  useEffect(() => {
    if (isDirectNav.current) {
      isDirectNav.current = false;
      return;
    }
    if (selection.source) {
      s3Content.loadSessions(selection.source);
    }
  }, [selection.source]);

  // Load titles when session changes (skip on direct nav)
  useEffect(() => {
    if (locationHandled.current) return;
    if (selection.source && selection.session) {
      s3Content.loadTitles(selection.source, selection.session);
    }
  }, [selection.session]);

  // Auto-select title when there's only one option (e.g., [Direct Content])
  useEffect(() => {
    if (locationHandled.current) return;
    if (s3Content.titles.length === 1 && selection.source && selection.session && !selection.title) {
      setSelection((prev) => ({ ...prev, title: s3Content.titles[0] }));
    }
  }, [s3Content.titles, selection.source, selection.session, selection.title]);

  // Load session content when title changes (skip on direct nav)
  useEffect(() => {
    if (locationHandled.current) return;
    if (selection.source && selection.session && selection.title) {
      s3Content.loadSessionContent(selection.source, selection.session, selection.title);
    }
  }, [selection.source, selection.session, selection.title]);

  const handleSelectionChange = useCallback((newSelection: BrowserSelection) => {
    // Once user manually changes selection, treat as normal browsing
    locationHandled.current = false;
    setSelection(newSelection);
  }, []);

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          View Transcriptions
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          Browse and view completed transcription results
        </p>
      </div>

      {/* Browser Panel */}
      <Card variant="elevated">
        <Card.Body>
          <HierarchicalBrowser
            sources={s3Content.sources}
            sessions={s3Content.sessions}
            titles={s3Content.titles}
            selection={selection}
            onSelectionChange={handleSelectionChange}
            isLoadingSources={s3Content.isLoadingSources}
            isLoadingSessions={s3Content.isLoadingSessions}
            isLoadingTitles={s3Content.isLoadingTitles}
          />
        </Card.Body>
      </Card>

      {/* Content Viewer */}
      <TranscriptionViewer
        content={s3Content.sessionContent}
        isLoading={s3Content.isLoadingSessionContent}
        error={s3Content.sessionContentError}
      />

      {/* Help Section */}
      {!selection.source && !searchParams.get('location') && (
        <Card variant="outlined">
          <Card.Body>
            <Stack gap="sm">
              <h3 style={{ margin: 0, ...textStyles.bodySmall, fontWeight: textStyles.label.fontWeight, color: textColor }}>
                Getting Started
              </h3>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: spacing.lg,
                  color: mutedColor,
                  fontSize: fontSize.sm,
                }}
              >
                <li>Select a source (streamer name) from the dropdown</li>
                <li>Choose a session (date) to view the transcription</li>
                <li>The audio player, summary, and segments will load automatically</li>
                <li>Expand segments to see detailed summaries and full transcripts</li>
              </ul>
            </Stack>
          </Card.Body>
        </Card>
      )}
    </Stack>
  );
}

export default ViewTranscriptionsPage;
